interface RingIconProps {
  readonly size: number;
  readonly aromatic?: boolean;
  /** Ring-edge indices (0-based, edge i joins vertex i to vertex i+1) to mark as double bonds. */
  readonly doubleBondEdges?: readonly number[];
}

export function RingIcon({ size, aromatic = false, doubleBondEdges = [] }: RingIconProps) {
  const center = 16;
  const radius = 11.5;
  const vertices = Array.from({ length: size }, (_, index) => {
    const angle = -Math.PI / 2 + (index * 2 * Math.PI) / size;
    return { x: center + radius * Math.cos(angle), y: center + radius * Math.sin(angle) };
  });
  const points = vertices.map((vertex) => `${vertex.x},${vertex.y}`).join(' ');

  // A double bond: a short stroke parallel to edge `i`, inset toward the ring centre.
  const doubleBondLine = (edge: number) => {
    const from = vertices[edge % size]!;
    const to = vertices[(edge + 1) % size]!;
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2;
    const toCentreX = center - midX;
    const toCentreY = center - midY;
    const toCentreLength = Math.hypot(toCentreX, toCentreY) || 1;
    const insetX = (toCentreX / toCentreLength) * 3.1;
    const insetY = (toCentreY / toCentreLength) * 3.1;
    return {
      x1: from.x + (to.x - from.x) * 0.22 + insetX,
      y1: from.y + (to.y - from.y) * 0.22 + insetY,
      x2: from.x + (to.x - from.x) * 0.78 + insetX,
      y2: from.y + (to.y - from.y) * 0.78 + insetY
    };
  };

  return (
    <svg
      className="ring-icon"
      viewBox="0 0 32 32"
      aria-hidden="true"
      focusable="false"
      data-ring-size={size}
      data-aromatic={aromatic || undefined}
    >
      <polygon points={points} />
      {aromatic ? <circle cx={center} cy={center} r="6.5" /> : null}
      {doubleBondEdges.map((edge) => {
        const segment = doubleBondLine(edge);
        return <line key={edge} x1={segment.x1} y1={segment.y1} x2={segment.x2} y2={segment.y2} />;
      })}
    </svg>
  );
}
